import { ReactNode, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SiteHeader from '@/components/SiteHeader'
import SiteFooter from '@/components/SiteFooter'
import Seo from '@/components/Seo'
import { signUpUrl } from '@/lib/auth'
import { captureEvent } from '@/lib/analytics'
import { LEARN_PAGES, LearnPageMeta, getLearnPage } from './learnPages'
import './learn.css'

export type { LearnPageMeta } from './learnPages'
export { LEARN_PAGES } from './learnPages'

interface LearnLayoutProps {
  page: LearnPageMeta
  children: ReactNode
}

export default function LearnLayout({ page, children }: LearnLayoutProps) {
  useEffect(() => {
    captureEvent('learn_page_viewed', { slug: page.slug })
    window.scrollTo(0, 0)
  }, [page.slug])

  const nextPage = page.next ? getLearnPage(page.next) : undefined
  const others = LEARN_PAGES.filter((p) => p.slug !== page.slug && p.slug !== nextPage?.slug)

  return (
    <div className="learn">
      <Seo
        title={page.metaTitle}
        description={page.metaDescription}
        path={page.path}
        image={`/static/og/${page.slug}.png`}
      />
      <SiteHeader />

      <header className="learn-header">
        <div className="learn-header-inner">
          <div className="learn-eyebrow">{page.eyebrow}</div>
          <h1>{page.title}</h1>
          <p className="learn-lede">
            <span className="learn-lede-label">The one-sentence version</span>
            {page.oneLiner}
          </p>
          {page.physicianReviewed && (
            <div className="learn-reviewed">
              <img src="/static/tiger-picture.png" alt="" loading="lazy" />
              <span>
                Written by <strong>Tiger Bawa, MD</strong> &middot; OB/GYN &middot; REI Fellow, Cleveland Clinic
              </span>
            </div>
          )}
        </div>
      </header>

      <main className="learn-article">
        <article className="learn-content">{children}</article>
      </main>

      {/* Directed next step — the primary onward pull */}
      <section className="learn-next" aria-label="Your next step">
        <div className="learn-next-inner">
          <div className="learn-eyebrow">Your next step</div>
          {nextPage ? (
            <Link to={nextPage.path} className="learn-next-card">
              <div className="learn-next-card-copy">
                <span className="learn-next-card-kicker">{nextPage.eyebrow}</span>
                <span className="learn-next-card-title">{nextPage.shortTitle}</span>
                <span className="learn-next-card-note">{page.nextNote ?? nextPage.oneLiner}</span>
              </div>
              <span className="learn-next-card-arrow" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </span>
            </Link>
          ) : (
            <p className="learn-next-readyline">
              You&rsquo;ve seen the science and the options. The next move is yours.
            </p>
          )}
          <div className="learn-next-intake">
            <p>
              Or skip ahead: complete an intake and a board certified physician reviews your
              presentation &mdash; from symptom to surgeon referral in 72 hours.
            </p>
            <a href={signUpUrl()} className="learn-next-intake-btn">
              Start your intake
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="learn-keep-reading" aria-label="Keep reading">
          <div className="learn-keep-reading-inner">
            <div className="learn-eyebrow">Also worth reading</div>
            <div className="learn-keep-reading-grid">
              {others.map((p) => (
                <Link key={p.slug} to={p.path} className="learn-crosslink">
                  <span className="learn-crosslink-title">{p.shortTitle}</span>
                  <span className="learn-crosslink-body">{p.oneLiner}</span>
                  <span className="learn-crosslink-cta">
                    Read the full explainer
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M5 12h14" />
                      <path d="m12 5 7 7-7 7" />
                    </svg>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  )
}
